import { useEffect, useState, useCallback, useMemo } from "react";
import { toast } from "react-toastify";
import MultiTable from "../../../Common/MUITable";
import AttendanceReportFilters from "./AttendanceReportFilters";
import AttendancePunchHistoryTable from "./AttendancePunchHistoryTable";
import { ExportViewAttendance } from "./ExportViewAttendance";
import {
    getTodayDate,
    validateAttendanceDateFilters,
} from "./attendanceReportDateUtils";
import { getDynamicColumnWidths } from "./attendanceColumnWidths";

import {
    GetAttendanceZones,
    GetAttendanceShifts,
    GetActiveDepartments,
    GetWardsByZone,
    ManageAttendance,
} from "../AttendanceApis";

// ─────────────────────────────────────────────────────────────────────────────

const STATUS_STYLES = {
    present: { label: "Present", bg: "#d1fae5", color: "#065f46" },
    absent:  { label: "Absent",  bg: "#fee2e2", color: "#991b1b" },
    late:    { label: "Late",    bg: "#fef3c7", color: "#92400e" },
};

// ─── Helpers ──────────────────────────────────────────────────────────────────
const unwrap = (res) => {
    if (Array.isArray(res)) return res;
    if (Array.isArray(res?.data)) return res.data;
    if (Array.isArray(res?.Data)) return res.Data;
    if (Array.isArray(res?.data?.data)) return res.data.data;
    if (Array.isArray(res?.data?.Data)) return res.data.Data;
    if (Array.isArray(res?.result)) return res.result;
    if (Array.isArray(res?.Result)) return res.Result;
    return [];
};

const formatTime = (raw) => {
    if (!raw) return "—";
    const timePart = String(raw).split(".")[0];
    const [hStr, mStr] = timePart.split(":");
    if (hStr === undefined || mStr === undefined) return raw;
    const h = parseInt(hStr, 10);
    const m = mStr.padStart(2, "0");
    const period = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    return `${String(h12).padStart(2, "0")}:${m} ${period}`;
};

const formatDate = (raw) => {
    if (!raw) return "—";
    const d = new Date(raw);
    if (isNaN(d)) return raw;
    return d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const getTextValue = (...values) => {
    const resolved = values.find((value) => value !== undefined && value !== null && value !== "");
    return resolved ?? "—";
};

// ─── StatusBadge ──────────────────────────────────────────────────────────────
const StatusBadge = ({ status }) => {
    const key = status?.toLowerCase();
    const style = STATUS_STYLES[key] ?? { label: status ?? "—", bg: "#e5e7eb", color: "#374151" };
    return (
        <span
            style={{
                backgroundColor: style.bg,
                color: style.color,
                padding: "3px 12px",
                borderRadius: "999px",
                fontWeight: 600,
                fontSize: "11px",
                whiteSpace: "nowrap",
            }}
        >
            {style.label}
        </span>
    );
};

// ─── Main Component ───────────────────────────────────────────────────────────
const ViewAttendanceReport = () => {
    // ── Dropdown options ──────────────────────────────────────────────────────
    const [zoneOptions, setZoneOptions]           = useState([]);
    const [wardOptions, setWardOptions]           = useState([]);
    const [shiftOptions, setShiftOptions]         = useState([]);
    const [departmentOptions, setDepartmentOptions] = useState([]);

    // ── Filters ───────────────────────────────────────────────────────────────
    const [filterData, setFilterData] = useState({
        ZoneId:        "",
        WardId:        "",
        EmployeeSearch: "",
        Status:        "",
        ShiftId:       "",
        DepartmentId:  "",   
        FromDate:      getTodayDate(),   // ← today
      ToDate:        getTodayDate(),
    });

    // ── Raw data from API ─────────────────────────────────────────────────────
    const [rawData, setRawData] = useState([]);
    const [isLoading, setIsLoading] = useState(false);

    // ── Server-side pagination ────────────────────────────────────────────────
    const [page, setPage] = useState(0);            // 0-based for MUITable
    const [pageSize, setPageSize] = useState(10);
    const [totalCount, setTotalCount] = useState(0);

    // ── Fetch dropdowns on mount ──────────────────────────────────────────────
    const fetchDropdowns = useCallback(async () => {
        try {
            const [zones, shifts, departments] = await Promise.all([
                GetAttendanceZones(),
                GetAttendanceShifts(),
                GetActiveDepartments(),
            ]);
            setZoneOptions(
                unwrap(zones).filter(
                    (zone) =>
                        zone.isActive !== false
                )
            );
            setShiftOptions(unwrap(shifts));
            setDepartmentOptions(unwrap(departments));
        } catch (err) {
            console.error("Dropdown fetch error:", err);
        }
    }, []);

    // ── Fetch attendance (server-side paged) ──────────────────────────────────
    const buildPayload = (filters, pageNumber, size) => {
        const activeFilters = Object.fromEntries(
            Object.entries(filters).filter(([, value]) => value !== "" && value !== null && value !== undefined)
        );
        return { ...activeFilters, PageNumber: pageNumber, PageSize: size };
    };

    const fetchAttendance = useCallback(
        async (filters = filterData, pageNumber = 1, size = pageSize) => {
            setIsLoading(true);
            try {
                const response = await ManageAttendance(buildPayload(filters, pageNumber, size));
                setRawData(unwrap(response));
                setTotalCount(Number(response?.totalCount ?? response?.TotalCount ?? 0));
            } catch (err) {
                console.error("Attendance fetch error:", err);
                toast.error("Failed to load attendance data.");
                setRawData([]);
                setTotalCount(0);
            } finally {
                setIsLoading(false);
            }
        },
        [filterData, pageSize]
    );

    const fetchAllAttendanceForExport = async () => {
        const response = await ManageAttendance(
            buildPayload(filterData, 1, Math.max(totalCount, pageSize, 1))
        );
        return unwrap(response);
    };

    const handlePageChange = (newPage) => {
        setPage(newPage);
        fetchAttendance(filterData, newPage + 1, pageSize);
    };

    const handleRowsPerPageChange = (newPageSize) => {
        setPageSize(newPageSize);
        setPage(0);
        fetchAttendance(filterData, 1, newPageSize);
    };

    useEffect(() => {
        fetchDropdowns();
        fetchAttendance(filterData, 1, pageSize);
    }, [fetchDropdowns]);

    useEffect(() => {
        if (zoneOptions.length === 0) return;

        setFilterData((previous) => {
            if (previous.ZoneId) return previous;

            const firstZoneId = String(zoneOptions[0].zoneId ?? zoneOptions[0].ZoneId);

            GetWardsByZone(firstZoneId)
                .then((wards) => {
                    setWardOptions(unwrap(wards));
                })
                .catch((error) => {
                    console.error("Ward fetch error:", error);
                    setWardOptions([]);
                });

            return { ...previous, ZoneId: firstZoneId };
        });
    }, [zoneOptions]);

    const attendanceData_filtered = rawData;

    // ── Handlers ──────────────────────────────────────────────────────────────
    const handleChange = async (field, value) => {
        if ((field === "FromDate" || field === "ToDate") && value) {
            const today = getTodayDate();
            if (value > today) {
                toast.error(
                    field === "FromDate"
                        ? "From Date cannot be a future date."
                        : "To Date cannot be a future date."
                );
                return;
            }
        }

        setFilterData((prev) => {
            const updated = { ...prev, [field]: value };
            if (field === "ZoneId") updated.WardId = "";
            return updated;
        });

        if (field === "ZoneId") {
            if (value) {
                try {
                    const wards = await GetWardsByZone(value);
                    setWardOptions(unwrap(wards));
                } catch (err) {
                    console.error("Ward fetch error:", err);
                    setWardOptions([]);
                }
            } else {
                setWardOptions([]);
            }
        }
    };

    const handleApplyFilters = (e) => {
        e.preventDefault();
        const dateValidationMessage = validateAttendanceDateFilters(filterData);
        if (dateValidationMessage) {
            toast.error(dateValidationMessage);
            return;
        }
        setPage(0);
        fetchAttendance(filterData, 1, pageSize);
    };

    const handleReset = () => {
        const cleared = {
            ZoneId: "", WardId: "", EmployeeSearch: "", Status: "",
            ShiftId: "", DepartmentId: "", FromDate: "", ToDate: "",
        };
        setFilterData(cleared);
        setWardOptions([]);
        setPage(0);
        fetchAttendance(cleared, 1, pageSize);
    };

    const expandTableThStyle = useMemo(
        () => ({
            backgroundColor: "#003366",
            color: "#fff",
            fontWeight: 600,
            padding: "8px",
            textAlign: "center",
            fontSize: "12px",
        }),
        []
    );

    const expandTableTdStyle = useMemo(
        () => ({
            backgroundColor: "#f9fafb",
            padding: "8px",
            textAlign: "center",
            fontSize: "12px",
            border: "1px solid #ddd",
        }),
        []
    );

    // ── Columns for MUITable ──────────────────────────────────────────────────
    const tableColumns = useMemo(
        () => [
            {
                label: "Emp Code",
                render: (row) => getTextValue(
                    row.employeeCode,
                    row.empCode,
                    row.EmployeeCode,
                    row.empId,
                    row.EmployeeId,
                    row.employeeId
                ),
            },
            {
                label: "Employee Name",
                render: (row) => getTextValue(row.employeeName, row.empName, row.EmployeeName, row.name),
            },
            {
                label: "Designation",
                render: (row) => getTextValue(
                    row.designationName,
                    row.DesignationName,
                    row.designation,
                    row.Designation,
                    row.roleName,
                    row.RoleName
                ),
            },
            {
                label: "Department",
                render: (row) => getTextValue(
                    row.departmentName,
                    row.DepartmentName,
                    row.department,
                    row.Department,
                    row.operationName,
                    row.OperationName
                ),
            },
            { label: "Master Shift", field: "masterShift" },
            { label: "Actual Shift", field: "actualShift" },
            { label: "Zone", field: "zoneName" },
            { label: "Ward", field: "circleName" },
            { label: "KPI Category", field: "kpiCategoryName" },
            { label: "Device ID", field: "deviceId" },
            { label: "Date", field: "date", render: (row) => formatDate(row.date) },
            { label: "Punch In", field: "punchIn", render: (row) => formatTime(row.punchIn) },
            { label: "Punch Out", field: "punchOut", render: (row) => formatTime(row.punchOut) },
            { label: "Total Time", field: "totalTime" },
            { label: "Status", field: "status", render: (row) => <StatusBadge status={row.status} /> },
        ],
        []
    );

    // Widths follow the data on the current page instead of fixed guesses.
    const sizedTableColumns = useMemo(
        () => getDynamicColumnWidths(tableColumns, attendanceData_filtered),
        [tableColumns, attendanceData_filtered]
    );

    // ─────────────────────────────────────────────────────────────────────────
    return (
        <>
            <div className="content text-start">
                <div className="page-content">

                    {/* ── Page Header ── */}
                    <div className="d-flex flex-column flex-md-row justify-content-between align-items-start gap-3 mt-3 mb-3">
                        <div className="me-md-3">
                            <h5 className="mb-1 fw-semibold">View Attendance Report</h5>
                            
                        </div>
                        <div className="d-flex flex-wrap gap-2 flex-shrink-0">
                            <ExportViewAttendance
                                attendanceData={attendanceData_filtered}
                                fetchExportData={fetchAllAttendanceForExport}
                            />
                        </div>
                    </div>

                    <AttendanceReportFilters
                        filterData={filterData}
                        zoneOptions={zoneOptions}
                        wardOptions={wardOptions}
                        shiftOptions={shiftOptions}
                        departmentOptions={departmentOptions}
                        onFilterChange={handleChange}
                        onApply={handleApplyFilters}
                        onReset={handleReset}
                    />

                    <div className="card shadow">
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <MultiTable
                                    loading={isLoading}
                                    needSRNumber
                                    columns={sizedTableColumns}
                                    rows={attendanceData_filtered}
                                    tableHeight={500}
                                    serverPagination
                                    totalCount={totalCount}
                                    page={page}
                                    rowsPerPage={pageSize}
                                    onPageChange={(_event, newPage) => handlePageChange(newPage)}
                                    onRowsPerPageChange={(event) =>
                                        handleRowsPerPageChange(parseInt(event.target.value, 10))
                                    }
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    hasExpandButton
                                    expandContent={(row) => (
                                        <AttendancePunchHistoryTable
                                            row={row}
                                            subThStyle={expandTableThStyle}
                                            subTdStyle={expandTableTdStyle}
                                        />
                                    )}
                                />
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        </>
    );
};
export default ViewAttendanceReport;